import { useEffect, useState } from "react";
import { AlertTriangle, Crown, Shield, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* ===== Module-strip card (compact) ===== */
export function SLAModuleCard({ data, onClick }) {
  if (!data) return null;
  const { compliance_pct = 100, behind = 0, total_leads = 0 } = data;
  if (total_leads === 0) return null;

  const tone =
    compliance_pct >= 90
      ? { ring: "border-emerald-200 bg-emerald-50/40 hover:border-emerald-400", chip: "bg-emerald-500" }
      : compliance_pct >= 70
        ? { ring: "border-amber-200 bg-amber-50/40 hover:border-amber-400", chip: "bg-amber-500" }
        : { ring: "border-rose-200 bg-rose-50/40 hover:border-rose-400", chip: "bg-rose-500" };

  return (
    <button
      onClick={onClick}
      data-testid="module-card-sla"
      className={`group flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${tone.ring}`}
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-white ${tone.chip}`}>
        <Shield className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
          SLA Compliance
        </div>
        <div className="font-display text-base font-semibold text-[#0A2540]">
          {compliance_pct}%{" "}
          {behind > 0 && (
            <span className="ml-1 text-xs font-normal text-rose-700">
              · {behind} behind
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* ===== Full SLA drawer body ===== */
export function SLAComplianceSheet() {
  const [sla, setSla] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/sla/today"),
        api.get("/admin/reports/attempt-compliance?period=today"),
      ]);
      setSla(s.data);
      setReport(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  }
  if (!sla) return <div className="p-6 text-sm text-rose-500">Failed to load</div>;

  return (
    <div className="space-y-6 px-1 py-2" data-testid="sla-panel">
      {/* Headline */}
      <div className="rounded-xl bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] p-5 text-white">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
          Outreach compliance · Today
        </div>
        <div className="mt-1 font-display text-4xl font-bold">
          {sla.compliance_pct}%
        </div>
        <div className="mt-1 text-xs opacity-80">
          {sla.on_track} on track · {sla.behind} behind · {sla.fresh} fresh
        </div>
        <div className="mt-3 h-1.5 rounded-full bg-white/15 overflow-hidden">
          <div
            className="h-full bg-[#C9A961]"
            style={{ width: `${sla.compliance_pct}%` }}
          />
        </div>
      </div>

      {/* Top breachers */}
      {sla.top_breachers?.length > 0 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-rose-700">
            <AlertTriangle className="h-3.5 w-3.5" />
            Top breachers
          </div>
          <div className="space-y-1.5">
            {sla.top_breachers.map((b) => (
              <div
                key={b.counsellor_id}
                data-testid={`sla-breacher-${b.counsellor_id}`}
                className="flex items-center justify-between rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm"
              >
                <span className="font-medium text-[#0A2540]">
                  {b.counsellor_name}
                </span>
                <Badge variant="outline" className="bg-white text-rose-700 border-rose-300">
                  {b.behind_count} behind
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Counsellor compliance table */}
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-slate-500">
          <TrendingUp className="h-3.5 w-3.5 text-[#C9A961]" />
          Counsellor Attempt Compliance
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Counsellor</th>
                <th className="px-2 py-2 text-right font-medium">Leads</th>
                <th className="px-2 py-2 text-right font-medium">Made</th>
                <th className="px-2 py-2 text-right font-medium">Req</th>
                <th className="px-2 py-2 text-right font-medium">SLA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report?.rows?.length ? (
                report.rows.map((r, i) => {
                  const ok = r.compliance_pct >= 90;
                  const warn = r.compliance_pct >= 70 && !ok;
                  return (
                    <tr
                      key={r.counsellor_id}
                      data-testid={`compliance-row-${r.counsellor_id}`}
                      className="hover:bg-slate-50/60"
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {i === 0 && r.compliance_pct >= 90 && (
                            <Crown className="h-3 w-3 text-[#C9A961]" />
                          )}
                          <span className="text-[#0A2540]">
                            {r.counsellor_name}
                          </span>
                        </div>
                        {r.behind_leads > 0 && (
                          <div className="mt-0.5 text-[10px] text-rose-600">
                            {r.behind_leads} leads behind
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-slate-700">
                        {r.leads_assigned}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-700">
                        {r.made_total}
                      </td>
                      <td className="px-2 py-2 text-right font-mono text-slate-600">
                        {r.required_total}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <span
                          className={`inline-block min-w-[42px] rounded px-1.5 py-0.5 text-center font-mono ${
                            ok
                              ? "bg-emerald-100 text-emerald-800"
                              : warn
                                ? "bg-amber-100 text-amber-800"
                                : "bg-rose-100 text-rose-800"
                          }`}
                        >
                          {r.compliance_pct}%
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-slate-400"
                  >
                    No compliance data yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-2 text-[10px] text-slate-400">
          Required = attempts that should have been completed by now per the
          cadence rule for each lead's temperature. Made = call/WA/SMS attempts
          logged. Auto-counted from dispositions, WhatsApp sends, and manual clicks.
        </div>
      </div>
    </div>
  );
}
